// "Was this browser just in this experience?" — the second way a session may skip the loader's Enter click.
//
// The click exists because starting the world needs a user gesture (autoplay policy) and because entering is
// a decision. `resumedSessionRef` already waives it for a reload REBIND: the seat survived the F5, the lease
// API says `sessionReady`, and asking a user who is mid-visit to press Enter again is nonsense.
//
// It waived nothing for the case measured on 2026-09-16 (event 034126a8, hub v1.13.0-28). The user reloaded
// three times inside 11 s; each unload RELEASED the seat, so the next page got a fresh `lease.create` instead
// of a rebind — no `sessionReady`, no rebind, no waiver. A spot interruption then moved him to another box.
// Frames were decoding at 20:11:23Z; `playStream` latched at 20:19:15Z, in the same second as the page's
// first click. Seven minutes and fifty-two seconds of "still loading" were a picture nobody was allowed to
// see. From the outside that is indistinguishable from a slow machine, and it is what got reported as one.
//
// So the waiver is widened to what the user actually did rather than to what the seat did: the SAME
// participant coming back into the SAME experience within a minute of having been in it. The mark is
// refreshed while the stream plays, so the window is measured from the last live frame, not from the entry.
//
// sessionStorage, not localStorage, on purpose: it is per TAB and dies with it, so this says "this tab was
// just in there", never "some window of this browser once was". Every access is guarded — Safari's private
// mode throws on `setItem`, and a storage failure must cost a click, never the entry.
export const RECENT_VISIT_KEY = 'myta-streams:recent-visit';

export const RECENT_VISIT_WINDOW_MS = 60_000;

// How often a playing page refreshes the mark. Well inside the window, so a tab that is torn down between two
// beats still counts as a return; cheap enough (one small sessionStorage write) to not be worth batching.
export const RECENT_VISIT_HEARTBEAT_MS = 15_000;

export interface IRecentVisit {
  eventId: string;
  userId: string;
  at: number;
}

export type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

const browserStorage = (): StorageLike | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

export const parseRecentVisit = (raw: string | null): IRecentVisit | null => {
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<IRecentVisit>;

    if (
      typeof parsed?.eventId !== 'string' ||
      typeof parsed?.userId !== 'string' ||
      typeof parsed?.at !== 'number' ||
      !Number.isFinite(parsed.at)
    ) {
      return null;
    }

    return { eventId: parsed.eventId, userId: parsed.userId, at: parsed.at };
  } catch {
    return null;
  }
};

// Both identities have to match, and a mark from the future is treated as no mark: a clock that jumped
// backwards would otherwise waive the click for the rest of the day.
export const isRecentVisit = (
  visit: IRecentVisit | null,
  eventId: string | null | undefined,
  userId: string | null | undefined,
  now: number,
  windowMs: number = RECENT_VISIT_WINDOW_MS,
): boolean => {
  if (!visit || !eventId || !userId) {
    return false;
  }

  if (visit.eventId !== eventId || visit.userId !== userId) {
    return false;
  }

  const age = now - visit.at;

  return age >= 0 && age <= windowMs;
};

export const markRecentVisit = (
  eventId: string | null | undefined,
  userId: string | null | undefined,
  now: number = Date.now(),
  storage: StorageLike | null = browserStorage(),
): void => {
  if (!eventId || !userId || !storage) {
    return;
  }

  try {
    storage.setItem(
      RECENT_VISIT_KEY,
      JSON.stringify({ eventId, userId, at: now } satisfies IRecentVisit),
    );
  } catch {
    // A browser that refuses storage costs this session one click, nothing else.
  }
};

export const hadRecentVisit = (
  eventId: string | null | undefined,
  userId: string | null | undefined,
  now: number = Date.now(),
  storage: StorageLike | null = browserStorage(),
  windowMs: number = RECENT_VISIT_WINDOW_MS,
): boolean => {
  if (!storage) {
    return false;
  }

  try {
    return isRecentVisit(
      parseRecentVisit(storage.getItem(RECENT_VISIT_KEY)),
      eventId,
      userId,
      now,
      windowMs,
    );
  } catch {
    return false;
  }
};

export const forgetRecentVisit = (
  storage: StorageLike | null = browserStorage(),
): void => {
  if (!storage) {
    return;
  }

  try {
    storage.removeItem(RECENT_VISIT_KEY);
  } catch {
    // Same reasoning as markRecentVisit.
  }
};
